import { AxiosPromise, AxiosResponse } from "axios";
import { oc } from "ts-optchain";

import { DealernetApi } from "./DealernetApi";

import {
    IAccountContext,
    IPartAlternativeDto,
    PartValidationReason,
} from "@/types";

export const CONTROLLER_PATH = "Backorder";

export class BackorderService {
    public static async getBackorders(data: ILoadAllBackOrdersForUserRequestDto): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/LoadAllBackOrdersForUser`,
            data,
        });
        
        const returnData = oc(response as AxiosResponse<any>).data([]);
        return returnData;
    }

    public static async getPartAlternatives(context: any, data: IBackOrdersAlternativesListRequestDto): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/AlternativesList`,
            data,
        });

        const returnData = oc(response as AxiosResponse<IPartAlternativeDto[]>).data([]);

        return returnData.map((item) => this.mapToPartAlternativeRowItem(item, context));
    }

    public static submitFollowUp(data: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SubmitFollowUp`,
            data,
        });
    }

    public static SubmitBackorderAdditionalDetails(data: any): AxiosPromise<any> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SubmitBackorderAdditionalDetails`,
            data,
        });
    }

    public static searchBackOrdersByDocumentOrPart(data: any): AxiosPromise<any> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SearchBackOrdersByDocumentOrPart`,
            data,
        });
    }

    private static mapToPartAlternativeRowItem(dto: IPartAlternativeDto, context: IGetPartAlternativeContext ): IGetPartAlternativesResultItem {
        const item = {
            oemPartCode: dto.AlternativeOemPartCode,
            partType: dto.AlternativePartType,
            price: null,
            currencyCode: dto.BillingCurrency,
            isContractPrice: false,
        };

        this.setPrice(item, dto, context);

        return item;
    }

    private static shouldHidePricing(dto: IPartAlternativeDto, context: IGetPartAlternativeContext): boolean {
        switch (dto.ValidationReason) {
            case PartValidationReason.IsChannelNotAuthorized:
            case PartValidationReason.IsCanceled:
            case PartValidationReason.IsSuperseded:
            case PartValidationReason.IsDiscontinued:
            case PartValidationReason.ItemNotPriced:
            case PartValidationReason.IsMaoNoPrice:
                return true;
            case PartValidationReason.IsSoftFrozen:
                if (
                    context.siteName === "MCFA" &&
                    dto.AlternativePicCode === "608"
                ) {
                    return true;
                }
                break;
        }

        return false;
    }

    private static setPrice(
        item: IGetPartAlternativesResultItem,
        dto: IPartAlternativeDto,
        context: IGetPartAlternativeContext
    ) {
   
        if (this.shouldHidePricing(dto, context)) {
            return;
        }

        if (context.canSeeContractPrice && context.canSeeDealerNet) {
            if (
                dto.ContractPrice &&
                dto.DealerNetPrice &&
                dto.ContractPrice > 0 &&
                dto.ContractPrice < dto.DealerNetPrice
            ) {
                item.price = dto.ContractPrice;
                item.isContractPrice = true;
                return;
            }

            item.price = dto.DealerNetPrice;
            return;
        }

        if (context.canSeeContractPrice) {
            item.price = dto.ContractPrice;
            item.isContractPrice = true;
            return;
        }

        if (context.canSeeDealerNet) {
            item.price = dto.DealerNetPrice;
            return;
        }
    }
}

export interface IGetPartAlternativeContext {
    siteName: string;
    canSeeDealerNet: boolean;
    canSeeContractPrice: boolean;
}

export interface IGetPartAlternativesRequest extends IAccountContext {
    oemPartCode: string;
    userAudiences: number[];
}

export interface IGetPartAlternativesResultItem {
    oemPartCode: string;
    partType: string;
    price: number | null;
    currencyCode: string;
    isContractPrice: boolean;
}

// tslint:disable-next-line: no-empty-interface
export interface IGetBackOrdersRequest extends IAccountContext {
    dealerCode: string;
    userAudiences: number[]
}

export interface ISubmitFollowUpRequest extends IAccountContext {
    dealerCode: string;
    email: string;
    fullName: string;
    message: string;
    orderNumber: string;
    partNumber: string;
    quantityBackordered: number;
    estimatedDate: string | Date;
    purchaseOrderNumber: string;
}

export interface ILoadAllBackOrdersForUserRequestDto {
    AccountId: number;
    UserId: number;
    UserAudiences: number[];
    PageSize: number[];
    PageNumber: number[];
    ReferenceNumber: string;
    OemPartCode: string;
    OrderType: string;
    FuzzySearch: boolean;
}

export interface IBackOrdersAlternativesListRequestDto {
    AccountId: number;
    UserId: number;
    OemPartCode: string;
    UserAudiences: number[];
}